import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { QueryKeys } from 'src/constants/QueryKeys'
import { useApply } from 'src/hooks/useAffiliate'
import { PublicProgram, Social } from 'src/types/Affiliate'
import { getReason } from 'src/utils/apiReason'
import styled from 'styled-components'
import { formatBps, formatCents, MONTHLY_PRICE_CENTS } from 'src/utils/affiliateFormat'
import { errorMessage } from './labels'
import {
    Card,
    CardTitle,
    Checkbox,
    ErrorText,
    Input,
    Label,
    LinkButton,
    Muted,
    ActionButton,
    Row,
    SecondaryButton,
    Select,
    TextArea,
} from './ui'

const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'X / Twitter', 'Discord', 'Twitch', 'Website', 'Other']

// The API wants full URLs; people usually paste "instagram.com/name".
const toUrl = (raw: string) => {
    const value = raw.trim()
    if (!value) return ''
    return /^https?:\/\//i.test(value) ? value : `https://${value}`
}

const isUrl = (value: string) => {
    try {
        const url = new URL(value)
        return /^https?:$/.test(url.protocol) && url.hostname.includes('.')
    } catch {
        return false
    }
}

const Pitch = styled.p`
    font-size: 30px;
    font-weight: 700;
    line-height: 1.25;
    max-width: 22em;

    @media only screen and (max-width: 600px) {
        font-size: 24px;
    }
`

/** What an affiliate earns, in dollars per referral rather than percentages. */
export const ProgramPitch = ({ program }: { program: PublicProgram }) => {
    const first = Math.floor((MONTHLY_PRICE_CENTS * program.firstMonthBps) / 10000)
    const recurring = Math.floor((MONTHLY_PRICE_CENTS * program.recurringBps) / 10000)

    return (
        <Card>
            <Pitch>
                Earn {formatCents(first)} for every person you refer, then {formatCents(recurring)} every month they
                stay subscribed.
            </Pitch>
            <Muted>
                That's {formatBps(program.firstMonthBps)} of their first month and {formatBps(program.recurringBps)} of
                every month after. Earnings unlock {program.holdDays} days after each payment, and you can withdraw
                to your bank through Stripe once you have {formatCents(program.minPayoutCents)}.
            </Muted>
        </Card>
    )
}

const ApplyForm = ({ program }: { program: PublicProgram }) => {
    const queryClient = useQueryClient()
    const apply = useApply()
    const [socials, setSocials] = useState<Social[]>([{ platform: PLATFORMS[0], url: '' }])
    const [audienceSize, setAudienceSize] = useState('')
    const [message, setMessage] = useState('')
    const [accepted, setAccepted] = useState(false)

    const filled = socials.filter((s) => s.url.trim())
    const badUrl = filled.find((s) => !isUrl(toUrl(s.url)))
    const canSubmit = accepted && filled.length > 0 && !badUrl && !apply.isPending

    const updateSocial = (index: number, patch: Partial<Social>) =>
        setSocials((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))

    const submit = () =>
        apply.mutate(
            {
                socials: filled.map((s) => ({ platform: s.platform, url: toUrl(s.url) })),
                audienceSize: audienceSize.trim() || undefined,
                message: message.trim() || undefined,
                acceptTerms: true,
                termsVersion: program.termsVersion,
            },
            {
                onError: (error) => {
                    // The terms changed while the form was open: reload them and ask again.
                    if (getReason(error) === 'terms_outdated') {
                        setAccepted(false)
                        queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateProgramPublic] })
                    }
                },
            }
        )

    return (
        <Card>
            <CardTitle>Apply to become an affiliate</CardTitle>
            <Muted>Tell us where you'd share SwiftSole. We review every application.</Muted>

            {socials.map((social, index) => (
                <Row key={index} $wrap={false}>
                    <Select
                        aria-label="Platform"
                        value={social.platform}
                        onChange={(e) => updateSocial(index, { platform: e.target.value })}
                        style={{ width: 150, flexShrink: 0 }}
                    >
                        {PLATFORMS.map((p) => (
                            <option key={p}>{p}</option>
                        ))}
                    </Select>
                    <Input
                        aria-label="Profile link"
                        placeholder="instagram.com/yourname"
                        value={social.url}
                        onChange={(e) => updateSocial(index, { url: e.target.value })}
                    />
                    {socials.length > 1 && (
                        <SecondaryButton
                            aria-label="Remove"
                            onClick={() => setSocials((prev) => prev.filter((_, i) => i !== index))}
                        >
                            ✕
                        </SecondaryButton>
                    )}
                </Row>
            ))}
            {badUrl && <ErrorText>"{badUrl.url}" doesn't look like a link.</ErrorText>}
            {socials.length < 10 && (
                <LinkButton onClick={() => setSocials((prev) => [...prev, { platform: PLATFORMS[0], url: '' }])}>
                    + Add another
                </LinkButton>
            )}

            <Label>
                Audience size (optional)
                <Input
                    placeholder="e.g. 12k followers"
                    maxLength={60}
                    value={audienceSize}
                    onChange={(e) => setAudienceSize(e.target.value)}
                />
            </Label>
            <Label>
                Anything else? (optional)
                <TextArea maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
            </Label>

            <Checkbox>
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                <span>
                    I agree to the{' '}
                    <a href="/affiliate/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#8fa0ff' }}>
                        affiliate terms
                    </a>{' '}
                    (version {program.termsVersion}).
                </span>
            </Checkbox>

            {apply.isError && <ErrorText>{errorMessage(apply.error)}</ErrorText>}

            <Row>
                <ActionButton disabled={!canSubmit} onClick={submit}>
                    {apply.isPending ? 'Submitting…' : 'Submit application'}
                </ActionButton>
            </Row>
        </Card>
    )
}

export default ApplyForm
